import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, ArrowLeft } from "lucide-react";
import jsPDF from "jspdf";

import { Navbar } from "@/components/layout/Nav";
import { CanalesLogo } from "@/components/CanalesLogo";
import { CertificateFrame, CertificateDivider, drawCertificateFrame, drawCertificateDivider, drawSeal } from "@/components/CertificateFrame";
import { useLang } from "@/context/LangContext";
import { api } from "@/api/client";

export default function Certificate() {
  const { name: nameParam } = useParams();
  const { lang } = useLang();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(
          `/public/onboarding/progress/${encodeURIComponent(nameParam)}`
        );
        setData(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [nameParam]);

  const download = () => {
    if (!data) return;
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();

    const NAVY = [13, 26, 63];
    const GOLD = [244, 163, 0];
    const RED = [227, 0, 15];

    // Frame
    drawCertificateFrame(doc, W, H, "gold");

    // Top: Canales logo plate
    const px = W / 2 - 110, py = 56;
    doc.setFillColor(...NAVY);
    doc.roundedRect(px, py, 220, 78, 12, 12, "F");
    // Roof squiggle
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(1.4);
    const roofPts = [];
    let lastX = px + 24, lastY = py + 26;
    for (let i = 0; i < 10; i++) {
      const nx = lastX + 18;
      const ny = lastY + (i % 2 === 0 ? -4 : 4);
      roofPts.push([nx - lastX, ny - lastY]);
      lastX = nx; lastY = ny;
    }
    doc.lines(roofPts, px + 24, py + 26, [1, 1], "S", false);
    // Logo text
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.setTextColor(255, 209, 0);
    doc.text("CANALES", W / 2, py + 44, { align: "center", charSpace: 3 });
    doc.setFontSize(18);
    doc.text("FURNITURE", W / 2, py + 66, { align: "center", charSpace: 3 });
    doc.setFillColor(...RED);
    doc.rect(px + 34, py + 72, 152, 3, "F");

    // TITLE
    doc.setFont("times", "bold");
    doc.setFontSize(52);
    doc.setTextColor(...NAVY);
    doc.text(
      lang === "es" ? "CERTIFICADO" : "CERTIFICATE",
      W / 2,
      py + 156,
      { align: "center", charSpace: 5 }
    );

    doc.setFont("helvetica", "normal");
    doc.setFontSize(15);
    doc.text(
      lang === "es" ? "DE EXCELENCIA" : "OF ACHIEVEMENT",
      W / 2,
      py + 180,
      { align: "center", charSpace: 8 }
    );

    // Ornament divider
    drawCertificateDivider(doc, W / 2, py + 200, GOLD);

    // Presented to
    doc.setFont("helvetica", "normal");
    doc.setFontSize(13);
    doc.setTextColor(60, 60, 60);
    doc.text(
      lang === "es" ? "Este certificado se otorga con orgullo a" : "This certificate is proudly presented to",
      W / 2,
      py + 234,
      { align: "center" }
    );

    // Name — script style
    doc.setFont("times", "bolditalic");
    doc.setFontSize(52);
    doc.setTextColor(...NAVY);
    doc.text(data.employee_name, W / 2, py + 288, { align: "center" });

    // Body paragraph
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(70, 70, 70);
    const body = lang === "es"
      ? [
          "En reconocimiento por completar con distinción la Ruta Canales de 9 exámenes,",
          "obteniendo calificaciones superiores al 90% en cada uno de ellos y demostrando",
          "un dominio excepcional del producto, servicio y trato al cliente.",
        ]
      : [
          "In recognition for completing with distinction the Canales 9-exam Route,",
          "scoring above 90% on each of them and demonstrating exceptional mastery",
          "of product, service and customer care.",
        ];
    body.forEach((ln, i) => doc.text(ln, W / 2, py + 316 + i * 15, { align: "center" }));

    // Achievement badge
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(...NAVY);
    doc.text(
      `${data.gold_exams}/${data.total_exams} · 90%+ · ${lang === "es" ? "EXPERTO CANALES" : "CANALES EXPERT"}`,
      W / 2,
      py + 376,
      { align: "center", charSpace: 2 }
    );

    // Seal + signature lines
    const sx = W / 2, sy = H - 90;
    drawSeal(
      doc, sx, sy,
      lang === "es" ? "EXPERTO" : "EXPERT",
      "CANALES",
      { GOLD, NAVY }
    );

    // Signature lines
    doc.setDrawColor(...NAVY);
    doc.setLineWidth(0.8);
    doc.line(sx - 260, H - 60, sx - 90, H - 60);
    doc.line(sx + 90, H - 60, sx + 260, H - 60);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...NAVY);
    doc.text(new Date().toLocaleDateString(), sx - 175, H - 64, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(lang === "es" ? "Fecha" : "Date", sx - 175, H - 48, { align: "center" });
    doc.text(
      lang === "es" ? "Dirección Canales Furniture" : "Canales Furniture Management",
      sx + 175,
      H - 48,
      { align: "center" }
    );

    doc.setFontSize(8);
    doc.setTextColor(...NAVY);
    doc.text("canalesfurniture.com", W / 2, H - 26, { align: "center" });

    doc.save(`canales-experto-${data.employee_name}.pdf`);
  };

  if (loading) return <div className="min-h-screen bg-halftone flex items-center justify-center font-display">Loading…</div>;

  if (!data) {
    return (
      <div className="min-h-screen bg-halftone">
        <Navbar />
        <div className="max-w-xl mx-auto brutal p-8 mt-10 bg-white text-center">
          <div className="font-display font-black text-2xl">404</div>
          <p className="font-body mt-2">{lang === "es" ? "Nombre no encontrado" : "Name not found"}</p>
        </div>
      </div>
    );
  }

  const eligible = data.golden;

  return (
    <div className="min-h-screen bg-halftone" data-testid="certificate-root">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-10">
        <button
          onClick={() => navigate(-1)}
          className="brutal-btn bg-white px-3 py-1 text-sm inline-flex items-center gap-1 mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> {lang === "es" ? "Volver" : "Back"}
        </button>

        {/* Certificate preview */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          className="rounded-lg overflow-hidden shadow-2xl"
        >
          <CertificateFrame variant="gold">
            <div className="flex flex-col items-center">
              <CanalesLogo width={180} className="drop-shadow-md" />
              <h1
                className="mt-6 font-serif font-black tracking-[0.35em] text-3xl sm:text-5xl"
                style={{ color: "#0d1a3f", fontFamily: "'Fredoka','Georgia',serif" }}
              >
                {lang === "es" ? "CERTIFICADO" : "CERTIFICATE"}
              </h1>
              <div
                className="mt-1 tracking-[0.5em] text-xs sm:text-sm font-bold"
                style={{ color: "#0d1a3f" }}
              >
                {lang === "es" ? "DE EXCELENCIA" : "OF ACHIEVEMENT"}
              </div>

              <CertificateDivider color="#f4a300" />

              <p className="mt-1 text-neutral-700 font-body text-xs sm:text-sm">
                {lang === "es" ? "Este certificado se otorga con orgullo a" : "This certificate is proudly presented to"}
              </p>

              <div
                className="mt-2 italic font-black text-3xl sm:text-5xl"
                style={{ color: "#0d1a3f", fontFamily: "'Great Vibes','Times New Roman', cursive" }}
              >
                {data.employee_name}
              </div>

              <p className="mt-4 font-body text-neutral-700 text-[11px] sm:text-sm leading-relaxed max-w-[80%]">
                {lang === "es"
                  ? "En reconocimiento por completar con distinción la Ruta Canales de 9 exámenes, obteniendo calificaciones superiores al 90% en cada uno y demostrando un dominio excepcional del producto, servicio y trato al cliente."
                  : "In recognition for completing with distinction the Canales 9-exam Route, scoring above 90% on each of them and demonstrating exceptional mastery of product, service and customer care."}
              </p>

              <div
                className="mt-3 inline-flex items-center gap-2 px-3 py-1 text-[10px] sm:text-xs font-display font-black tracking-[0.25em] text-white rounded-full"
                style={{ background: "#0d1a3f" }}
              >
                {data.gold_exams}/{data.total_exams} · 90%+ · {lang === "es" ? "EXPERTO CANALES" : "CANALES EXPERT"}
              </div>
            </div>
          </CertificateFrame>
        </motion.div>

        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          {eligible ? (
            <button
              onClick={download}
              data-testid="certificate-download-btn"
              className="brutal-btn bg-canales-red text-white px-6 py-3 flex items-center gap-2 text-lg font-display font-black"
            >
              <Download className="w-5 h-5" /> {lang === "es" ? "Descargar Certificado PDF" : "Download Certificate PDF"}
            </button>
          ) : (
            <div className="brutal p-4 bg-canales-cream max-w-2xl">
              <div className="font-display font-black text-xl mb-1">
                {lang === "es" ? "Aún no calificas" : "Not eligible yet"}
              </div>
              <p className="font-body">
                {lang === "es"
                  ? `Actualmente ${data.gold_exams}/${data.total_exams} exámenes con 90%+. Sigue practicando para desbloquear el certificado dorado.`
                  : `Currently ${data.gold_exams}/${data.total_exams} exams with 90%+. Keep training to unlock the golden certificate.`}
              </p>
              <Link to="/onboarding" className="mt-3 inline-flex brutal-btn bg-canales-blue text-white px-4 py-2 text-sm">
                {lang === "es" ? "Ver mi ruta" : "See my path"}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
